import type { ChangeRequestApprovalState, PaginatedCollection, Repository, ServerTaskApprovalBffResponse } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { ServerTaskApprovals } from "~/areas/approvals/components/ServerTaskApprovals/ServerTaskApprovals";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { PageContent } from "~/components/PageContent/PageContent";
export interface ApprovalsPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    providerId?: string;
    status?: string;
    spaces: string[];
}
export interface ApprovalsPageLoaderData {
    approvalsListData: ApprovalsListData;
    approvalStats: {
        hasAnyApprovals: boolean;
        pendingCount: number;
        completedCount: number;
    };
}
interface ApprovalsListData {
    approvals: PaginatedCollection<ServerTaskApprovalBffResponse>;
}
export const approvalsPageTitle = "Approvals";
const defaultPageSize = 30;
export async function ApprovalsPageLoader(repository: Repository, queryParams: ApprovalsPageQueryParams): Promise<ApprovalsPageLoaderData> {
    if (!isFeatureToggleEnabled("octopus-approvals")) {
        return {
            approvalsListData: {
                approvals: { Items: [], TotalResults: 0, ItemsPerPage: 0 },
            },
            approvalStats: { hasAnyApprovals: false, pendingCount: 0, completedCount: 0 },
        };
    }
    const approvalsPromise = getPaginatedApprovals(repository, queryParams.page, queryParams.pageSize, queryParams.providerId, queryParams.status);
    const countsPromise = repository.ServerTaskApprovals.countsBff(queryParams.providerId);
    const [approvalsData, counts] = await Promise.all([approvalsPromise, countsPromise]);
    const pendingCount = counts.PreApproval;
    const completedCount = counts.Approved + counts.PostApproval;
    const hasAnyApprovals = pendingCount + completedCount > 0;
    return {
        approvalsListData: approvalsData,
        approvalStats: {
            hasAnyApprovals,
            pendingCount,
            completedCount,
        },
    };
}
async function getPaginatedApprovals(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, providerId?: string, status?: string, spaces: string[] = []): Promise<ApprovalsListData> {
    const stateFilter: undefined | ChangeRequestApprovalState = !status || status === "all" ? undefined : status === "pending" ? "preapproval" : "approved";
    const skip = (page - 1) * pageSize;
    const requestParams = {
        skip,
        take: pageSize,
        providerId,
        state: stateFilter,
        spaces,
    };
    const approvalsResponse = await repository.ServerTaskApprovals.listBff(requestParams);
    return {
        approvals: approvalsResponse.ServerTaskApprovals,
    };
}
interface ServerTaskApprovalsPageProps {
    queryParams: ApprovalsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ApprovalsPageQueryParams>;
    loaderData: ApprovalsPageLoaderData;
}
export default function ServerTaskApprovalsPage({ queryParams, setQueryParams, loaderData }: ServerTaskApprovalsPageProps) {
    if (!isFeatureToggleEnabled("octopus-approvals")) {
        return <></>;
    }
    return (<PageContent header={{ title: approvalsPageTitle }}>
            <ServerTaskApprovals queryParams={queryParams} setQueryParams={setQueryParams} loaderData={loaderData}/>
        </PageContent>);
}
